import { useCallback, useEffect, useRef, useState } from 'react';
import {
  runGenerationPipeline,
  type OrchestratorHandle,
  type GenerationInput,
} from '../services/carouselGenerationOrchestrator';
import { saveProject } from '../services/projectService';
import {
  refineCarouselContent,
  generateSlideImage,
  IMAGE_MODEL_FLASH,
  TEXT_MODEL_PRO,
} from '../services/geminiService';
import {
  AspectRatio,
  CarouselStyle,
  CarouselProject,
  Profile,
  Slide,
  UploadedDocument,
} from '../types';
import { generateId } from '../lib/utils';
import { extractProjectNameFromCover } from '../lib/projectName';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';

type ImageJobState = 'pending' | 'running' | 'done' | 'failed';

export type ChatState =
  | { status: 'empty' }
  | { status: 'generating'; input: GenerationInput }
  | {
      status: 'ready';
      project: CarouselProject;
      imageJobs: Record<string, ImageJobState>;
    }
  | { status: 'error'; message: string };

export interface SendInput {
  prompt: string;
  document?: UploadedDocument;
  style: CarouselStyle;
  slideCount: number;
  modelName: string;
  avatarContext?: string;
  profile: Profile;
}

const DEFAULT_ACCENT = '#4763CA';

export function useChatCarousel() {
  const { user } = useAuth();
  const { language } = useTranslation();
  const [state, setState] = useState<ChatState>({ status: 'empty' });
  const handleRef = useRef<OrchestratorHandle | null>(null);
  const projectRef = useRef<CarouselProject | null>(null);

  const cancelPipeline = useCallback(() => {
    handleRef.current?.cancel();
    handleRef.current = null;
  }, []);

  useEffect(() => cancelPipeline, [cancelPipeline]);

  const reset = useCallback(() => {
    cancelPipeline();
    projectRef.current = null;
    setState({ status: 'empty' });
  }, [cancelPipeline]);

  const send = useCallback(
    (input: SendInput) => {
      if (!user) return;
      cancelPipeline();

      // Generate the project id client-side so we can tell the server to
      // persist the project atomically with generation. If the tab closes
      // mid-request, the server still completes the save and the user
      // sees the carousel in their project list on reload.
      const projectId = generateId();
      const aspectRatio: '1/1' | '4/5' = '4/5';

      const genInput: GenerationInput = {
        prompt: input.prompt,
        document: input.document,
        style: input.style,
        slideCount: input.slideCount,
        modelName: input.modelName,
        avatarContext: input.avatarContext,
        language,
        save: {
          projectId,
          style: input.style,
          aspectRatio,
          profile: input.profile as unknown as Record<string, any>,
          language,
        },
      };

      setState({ status: 'generating', input: genInput });

      handleRef.current = runGenerationPipeline(genInput, {
        onTextStart: () => {},
        onTextReady: async (result) => {
          const generatedName =
            result.savedProject?.name || extractProjectNameFromCover(result.slides);
          const project: CarouselProject = {
            id: projectId,
            name: generatedName || 'Novo carrossel',
            style: input.style,
            aspectRatio: '4/5',
            profile: input.profile,
            slides: result.slides,
            theme: input.style === CarouselStyle.STORYTELLER ? 'DARK' : 'LIGHT',
            accentColor: DEFAULT_ACCENT,
            showAccent: true,
            showSlideNumbers: true,
            showVerifiedBadge: true,
            headerScale: 1.0,
            fontStyle: 'MODERN',
            fontScale: 1.0,
            globalImageStyle: '',
            nameSource: 'cover_auto',
            language,
            carouselFormat: result.format,
            analisePreCriacao: result.analisePreCriacao,
            checklistValidacao: result.checklistValidacao,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          projectRef.current = project;

          // Only track jobs the orchestrator will actually process. A slide
          // flagged showImage=true but missing a prompt (e.g. when the
          // distribution post-processor forced the flag but the AI skipped
          // the prompt) would otherwise sit on 'pending' forever and render
          // an infinite shimmer in the thumbnail.
          const imageJobs: Record<string, ImageJobState> = Object.fromEntries(
            result.slides
              .filter((s) => s.showImage && !!s.imagePrompt && s.imagePrompt.trim().length > 0)
              .map((s) => [s.id, 'pending' as ImageJobState]),
          );

          setState({ status: 'ready', project, imageJobs });

          // Server already persisted the project atomically with generation
          // (see /api/ai/generate `save` block). Only fall back to a client
          // save if the server skipped it — e.g. the inline save failed on
          // the backend, in which case savedProject is absent.
          if (!result.savedProject) {
            try {
              await saveProject(project, user.id);
            } catch {
              /* non-blocking: project survives in memory, retry on Edit/Export */
            }
          }
        },
        onImageJobStart: (slideId) => {
          setState((prev) => {
            if (prev.status !== 'ready') return prev;
            return { ...prev, imageJobs: { ...prev.imageJobs, [slideId]: 'running' } };
          });
        },
        onImageJobDone: (slideId, imageUrl) => {
          setState((prev) => {
            if (prev.status !== 'ready') return prev;
            const nextSlides: Slide[] = prev.project.slides.map((s) =>
              s.id === slideId ? { ...s, imageUrl } : s,
            );
            const nextProject: CarouselProject = {
              ...prev.project,
              slides: nextSlides,
              updatedAt: new Date().toISOString(),
            };
            projectRef.current = nextProject;
            if (user) {
              saveProject(nextProject, user.id).catch(() => {
                /* non-blocking: in-memory state is canonical, editor can retry */
              });
            }
            return {
              ...prev,
              project: nextProject,
              imageJobs: { ...prev.imageJobs, [slideId]: 'done' },
            };
          });
        },
        onImageJobFailed: (slideId) => {
          setState((prev) => {
            if (prev.status !== 'ready') return prev;
            return { ...prev, imageJobs: { ...prev.imageJobs, [slideId]: 'failed' } };
          });
        },
        onAllDone: () => {
          /* no-op: individual callbacks already patched state */
        },
        onError: (_stage, error) => {
          setState({ status: 'error', message: error.message });
        },
      });
    },
    [cancelPipeline, language, user],
  );

  /**
   * Refines a single slide by natural-language instruction.
   * - Calls refineCarouselContent with slideIndex (patches one slide).
   * - Patches state with the refined slides.
   * - If the slide's imagePrompt changed AND showImage is still true,
   *   regenerates its image. Otherwise keeps the existing image.
   */
  const refineSlide = useCallback(
    async (slideIndex: number, instruction: string): Promise<void> => {
      if (!user) throw new Error('No authenticated user');
      if (state.status !== 'ready') throw new Error('Carrossel não está pronto para ajustes');
      if (!instruction.trim()) throw new Error('Instrução vazia');

      const currentProject = state.project;
      const previousSlide = currentProject.slides[slideIndex];
      if (!previousSlide) throw new Error(`Slide ${slideIndex + 1} não encontrado`);

      // Mark the single slide as "running" in imageJobs so the thumbnail
      // shows the shimmer state while refinement runs. We reuse the image-job
      // state machine for visual feedback; the actual image fetch happens
      // after refineCarouselContent returns.
      setState((prev) => {
        if (prev.status !== 'ready') return prev;
        return {
          ...prev,
          imageJobs: { ...prev.imageJobs, [previousSlide.id]: 'running' },
        };
      });

      let refinedSlides: Slide[];
      try {
        refinedSlides = await refineCarouselContent(
          currentProject.slides,
          instruction,
          slideIndex,
          TEXT_MODEL_PRO,
          language,
        );
      } catch (error) {
        setState((prev) => {
          if (prev.status !== 'ready') return prev;
          return {
            ...prev,
            imageJobs: { ...prev.imageJobs, [previousSlide.id]: 'failed' },
          };
        });
        throw error instanceof Error ? error : new Error(String(error));
      }

      const refinedSlide = refinedSlides[slideIndex];
      const promptChanged =
        (refinedSlide.imagePrompt || '') !== (previousSlide.imagePrompt || '');
      const needsImageRegen = refinedSlide.showImage && promptChanged && !!refinedSlide.imagePrompt;

      // If image regen is not required, preserve the previous imageUrl so the
      // thumbnail doesn't blink back to shimmer for nothing.
      const patchedSlides: Slide[] = refinedSlides.map((s, i) =>
        i === slideIndex && !needsImageRegen
          ? { ...s, imageUrl: previousSlide.imageUrl }
          : s,
      );

      const nextProject: CarouselProject = {
        ...currentProject,
        slides: patchedSlides,
        updatedAt: new Date().toISOString(),
      };
      projectRef.current = nextProject;

      setState((prev) => {
        if (prev.status !== 'ready') return prev;
        return {
          ...prev,
          project: nextProject,
          imageJobs: {
            ...prev.imageJobs,
            [previousSlide.id]: needsImageRegen ? 'running' : 'done',
          },
        };
      });

      saveProject(nextProject, user.id).catch(() => {});

      if (!needsImageRegen) return;

      try {
        const aspectRatio: AspectRatio = nextProject.aspectRatio ?? '4/5';
        const imageUrl = await generateSlideImage(
          refinedSlide.imagePrompt!,
          aspectRatio,
          IMAGE_MODEL_FLASH,
          nextProject.globalImageStyle ?? '',
        );
        setState((prev) => {
          if (prev.status !== 'ready') return prev;
          const next = prev.project.slides.map((s, i) =>
            i === slideIndex ? { ...s, imageUrl } : s,
          );
          const np: CarouselProject = {
            ...prev.project,
            slides: next,
            updatedAt: new Date().toISOString(),
          };
          projectRef.current = np;
          if (user) saveProject(np, user.id).catch(() => {});
          return {
            ...prev,
            project: np,
            imageJobs: { ...prev.imageJobs, [previousSlide.id]: 'done' },
          };
        });
      } catch (error) {
        setState((prev) => {
          if (prev.status !== 'ready') return prev;
          return {
            ...prev,
            imageJobs: { ...prev.imageJobs, [previousSlide.id]: 'failed' },
          };
        });
        throw error instanceof Error ? error : new Error(String(error));
      }
    },
    [state, user, language],
  );

  return { state, send, refineSlide, reset, cancel: cancelPipeline };
}
